import React, { useMemo, useState } from 'react'

import { ThemeProvider } from '@mui/material'
import { getTheme } from 'themes'

import Button from 'components_new/atoms/Button'

import Breadcrumbs from 'components_new/molecules/Breadcrumbs'
import GenericListItem from 'components_new/molecules/GenericListItem'
import SimpleDialog from 'components_new/molecules/SimpleDialog'
import SimpleTable from 'components_new/molecules/SimpleTable'

import { TSlice, TInUseDetails } from 'types/GlobalSlice'
import { TFunction, useTranslation } from 'language/translator'

import { useMetrics } from 'redux/hooks/Metrics'
import { sortAlphabeticalAsc } from 'utils/sortHelper'
import { arrayToObject } from 'utils/functions'

interface DeleteSliceDialogProps {
  handleClose: () => void
  handleDelete: (callback: () => void) => void
  open: boolean
  slice: TSlice | null
  type?: 'slice' | 'filter'
}

const ContentText = ({
  slice,
  t,
  type
}: {
  slice: TSlice
  t: TFunction
  type: DeleteSliceDialogProps['type']
}) => {
  if (slice.in_use.length === 0 && type === 'slice') {
    // allowed to delete
    return (
      <>
        <code>{slice.name}</code>
        {t('global', 'will_be_deleted_are_you_sure')}
      </>
    )
  }

  if (slice.in_use.length === 0) {
    // allowed to delete filter
    return <>Filter {t('global', 'will_be_deleted_are_you_sure')}</>
  }

  return (
    <>
      <code>{slice.name}</code>{' '}
      {type === 'slice'
        ? t('entities', 'slice_in_use')
        : t('entities', 'filter_in_use')}
      .
    </>
  )
}

const DeleteSliceDialog = (props: DeleteSliceDialogProps) => {
  const { handleClose, handleDelete, open, slice, type = 'slice' } = props

  const [loading, setLoading] = useState(false)

  const [metrics] = useMetrics()

  const { t, lang } = useTranslation()

  const inUse = useMemo(() => {
    if (!slice || slice.in_use.length === 0) {
      return []
    }

    const metricMapper = arrayToObject(metrics)

    return slice.in_use
      .map((item) => {
        const metric = metricMapper[item.metric.id]

        return {
          id: item.metric.id,
          title: metric?.title[lang],
          details: item
        }
      })
      .sort((a, b) => sortAlphabeticalAsc(a.title, b.title))
  }, [metrics, slice?.in_use])

  const parsedHandleDelete = () => {
    setLoading(true)
    handleDelete(() => setLoading(false))
  }

  if (!slice) {
    return null
  }

  const disabled = type === 'slice' && slice.in_use.length > 0

  return (
    <ThemeProvider theme={getTheme('light')}>
      <SimpleDialog
        title={`${t('global', 'delete')} ${
          type === 'slice'
            ? t('entities', 'slice', true)
            : t('filters', 'filter', true)
        }?`}
        open={open}
        onClose={handleClose}
        actions={
          <>
            <Button variant="text" onClick={handleClose}>
              {t('global', 'cancel')}
            </Button>
            <Button
              color="error"
              disabled={disabled}
              loading={loading}
              onClick={parsedHandleDelete}
              variant="contained"
            >
              {t('global', 'delete')}
            </Button>
          </>
        }
        contentText={<ContentText slice={slice} type={type} t={t} />}
        sx={{
          zIndex: 1302 // for fullSize
        }}
      >
        {inUse.length > 0 ? (
          <SimpleTable
            columns={[
              { title: t('metrics', 'metric') },
              { title: t('global', 'location') }
            ]}
            rows={inUse.map((item, index) => ({ cells: [
              <GenericListItem
                key={index}
                size="small"
                title={item.title}
                variant="slice"
              />,
              <PathBreadcrumbs details={item.details} key={index} t={t} />
            ] }))}
            sx={{ mt: 2 }}
          />
        ) : null}
      </SimpleDialog>
    </ThemeProvider>
  )
}

const PathBreadcrumbs = (props: { details: TInUseDetails; t: TFunction }) => {
  const { details, t } = props

  if ('dashboard' in details) {
    return (
      <Breadcrumbs
        highlightCurrent={true}
        items={[
          {
            icon: { name: 'Dashboards' },
            title: t('global', 'dashboards')
          },
          {
            title: details.dashboard.title
          },
          {
            href: `/dashboards/${details.tab.id}`,
            newTab: true,
            title: details.tab.title
          }
        ]}
      />
    )
  }

  if ('group' in details) {
    return (
      <Breadcrumbs
        highlightCurrent={true}
        items={[
          {
            icon: { name: 'Groups' },
            title: t('global', 'groups')
          },
          {
            href: `/groups/${details.group.id}`,
            newTab: true,
            title: details.group.title
          }
        ]}
      />
    )
  }

  return (
    <Breadcrumbs
      highlightCurrent={true}
      items={[
        {
          href: '/key-metrics',
          icon: { name: 'KeyMetrics' },
          newTab: true,
          title: t('key_metrics', 'key_metric')
        }
      ]}
    />
  )
}

export default DeleteSliceDialog
